import { visibleNavItems } from './rbac'

/**
 * Single navigation column: ordered, collapsible groups.
 *
 * Every item still comes from rbac.visibleNavItems(), so permissions and module
 * gates remain the single source of truth. This file only decides presentation:
 * which group a visible item belongs to and the order groups appear in.
 */
export const NAV_GROUPS = [
  { key: 'overview', label: 'Overview', icon: 'lucide-house' },
  { key: 'sacraments', label: 'Sacraments & Mass', icon: 'lucide-church' },
  { key: 'pastoral', label: 'Pastoral care', icon: 'lucide-heart-handshake' },
  { key: 'people', label: 'People', icon: 'lucide-users' },
  { key: 'finance', label: 'Finance', icon: 'lucide-wallet' },
  { key: 'parish-life', label: 'Parish life', icon: 'lucide-calendar-days' },
  { key: 'administration', label: 'Administration', icon: 'lucide-settings' },
  { key: 'my-church', label: 'My church', icon: 'lucide-user' },
]

const ROUTE_GROUP = {
  '/': 'overview',
  '/sacraments': 'sacraments',
  '/mass-intentions': 'sacraments',
  '/mass-calendar': 'sacraments',
  '/mass-schedules': 'sacraments',
  '/gregorian-series': 'sacraments',
  '/sacristy': 'sacraments',
  '/today': 'sacraments',
  '/reporting': 'sacraments',
  '/prayer-requests': 'pastoral',
  '/welfare': 'pastoral',
  '/ministry': 'pastoral',
  '/formation': 'pastoral',
  '/compliance': 'pastoral',
  '/members': 'people',
  '/households': 'people',
  '/admin/profile-requests': 'people',
  '/staff': 'people',
  '/giving': 'finance',
  '/pledges': 'finance',
  '/giving/unassigned': 'finance',
  '/funds': 'finance',
  '/campaigns': 'finance',
  '/expenses': 'finance',
  '/expenses/summary': 'finance',
  '/disbursements': 'finance',
  '/fund-transfers': 'finance',
  '/giving/statement': 'finance',
  '/fund-types': 'finance',
  '/events': 'parish-life',
  '/messaging': 'parish-life',
  '/media': 'parish-life',
  '/facility-bookings': 'parish-life',
  '/facilities/assets': 'parish-life',
  '/inventory': 'parish-life',
  '/settings': 'administration',
  '/settings/permissions': 'administration',
  '/site-pages': 'administration',
  '/templates': 'administration',
  '/admin/users': 'administration',
  '/plan': 'administration',
  '/admin/tenants': 'administration',
  '/my': 'my-church',
  '/mass-schedule-public': 'my-church',
  '/mass-request': 'my-church',
  '/my/giving': 'my-church',
  '/my/pledges': 'my-church',
  '/my/statement': 'my-church',
  '/my/events': 'my-church',
  '/my/prayer': 'my-church',
  '/my/certificates': 'my-church',
  '/my/formation': 'my-church',
  '/my/household': 'my-church',
  '/my/profile': 'my-church',
}

// Merged destinations now live behind in-page tabs / sub-nav.
const MASS_ROUTES = ['/mass-calendar', '/mass-schedules', '/mass-intentions', '/gregorian-series']
const SETTINGS_ROUTES = [
  '/settings',
  '/settings/permissions',
  '/admin/users',
  '/site-pages',
  '/templates',
  '/plan',
  '/admin/tenants',
]
// Moved to the top-bar help menu, so they never render in the sidebar.
const HELP_ROUTES = new Set(['/support', '/whats-new'])

function groupOf(item) {
  if (ROUTE_GROUP[item.route]) return ROUTE_GROUP[item.route]
  if (item.section === 'member') return 'my-church'
  if (item.section === 'admin') return 'administration'
  // Never drop a route because a new item was added without a mapping.
  return 'people'
}

/**
 * Replace a set of routes with one parent entry. The parent lights up for every
 * route in `matchRoutes` so the sidebar stays accurate on deep links.
 */
function takeParent(items, routes, parent) {
  const members = routes.map((route) => items.find((item) => item.route === route)).filter(Boolean)
  if (!members.length) return { rest: items, parent: null }
  const route = members.some((item) => item.route === parent.route) ? parent.route : members[0].route
  return {
    rest: items.filter((item) => !routes.includes(item.route)),
    parent: { ...members[0], ...parent, route, matchRoutes: routes },
  }
}

function buildGroups(items) {
  const byGroup = new Map(NAV_GROUPS.map((group) => [group.key, []]))
  for (const item of items) {
    const key = groupOf(item)
    if (!byGroup.has(key)) byGroup.set(key, [])
    byGroup.get(key).push(item)
  }
  return NAV_GROUPS.map((group) => ({ ...group, items: byGroup.get(group.key) || [] })).filter(
    (group) => group.items.length,
  )
}

/** One ordered list of collapsible groups for the current user. */
export function navGroups() {
  const items = visibleNavItems().filter((item) => !HELP_ROUTES.has(item.route))
  const mass = takeParent(items, MASS_ROUTES, {
    key: 'nav-mass',
    label: 'Mass',
    icon: 'lucide-church',
    route: '/mass-calendar',
  })
  const settings = takeParent(mass.rest, SETTINGS_ROUTES, {
    key: 'nav-settings',
    label: 'Settings',
    icon: 'lucide-settings',
    route: '/settings',
  })
  // Both merged parents must be fed back in; dropping one silently removes its routes.
  const mergedParents = [mass.parent, settings.parent].filter(Boolean)
  return buildGroups([...settings.rest, ...mergedParents])
}

/** Normalise app routes for active-state comparison. */
export function normaliseRoute(path) {
  const target = path || '/'
  if (target.startsWith('/church')) return target.slice('/church'.length) || '/'
  return target
}

/** True for the current route or a descendant of it. */
export function isActiveRoute(path, currentPath) {
  const current = normaliseRoute(currentPath)
  const target = normaliseRoute(path)
  if (target === '/') return current === '/' || current === ''
  return current === target || current.startsWith(`${target}/`)
}

export function isNavItemActive(item, currentPath) {
  return (
    isActiveRoute(item.route, currentPath) ||
    (item.matchRoutes || []).some((path) => isActiveRoute(path, currentPath))
  )
}

export function groupHasActive(group, currentPath) {
  return group.items.some((item) => isNavItemActive(item, currentPath))
}
